import { useMemo, type MouseEvent } from 'react'
import { File } from '@phosphor-icons/react'
import type { IconComponent } from '@/design-system'
import { useT, type Localized } from '@/i18n'
import { pageIcon } from '@/navigation/pageIcons'
import { NAV_TREE } from '@/navigation/tree'
import { findNav } from '@/navigation/useNavigation'
import { useTabs } from '@/stores/tabs'

/** Calm section hues for the tab icon tiles (one per navigation section, stable across reloads). */
const HUES = ['#2f6bd8', '#12a06a', '#c47a14', '#7c5cd6', '#0e9aa7', '#d9486b', '#4f6d8f', '#c2410c', '#15803d', '#9333ea']
const hueOf = (id: string) => HUES[[...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % HUES.length]!

export interface TabMeta {
  title: string
  /** what tells two tabs of the same screen apart (the AWB, the record id…) */
  hint: string
  icon: IconComponent
  hue: string
  section: string
}

const clip = (s: string, n = 14) => (s.length > n ? `${s.slice(0, n - 1)}…` : s)

function decode(s: string) {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

export function tabMeta(url: string, l: (v: Localized) => string): TabMeta {
  const u = new URL(url, 'http://aos.local')
  const { pathname, search } = u
  let nav = findNav(pathname, search)
  let hint = ''
  if (!nav) {
    // a detail screen outside the menu (e.g. /operations/drs/12): name it after its list, plus the id
    const parts = pathname.split('/').filter(Boolean)
    for (let i = parts.length - 1; i > 0 && !nav; i--) {
      nav = findNav(`/${parts.slice(0, i).join('/')}`, '')
      if (nav) hint = decode(parts[i]!)
    }
  } else if (nav.page.path.includes(':')) {
    hint = decode(pathname.split('/').filter(Boolean).pop() ?? '')
  }
  if (!hint) {
    const own = nav?.page.path.split('?')[1] ?? ''
    for (const [k, v] of u.searchParams) {
      if (v && !own.includes(`${k}=`)) {
        hint = v
        break
      }
    }
  }
  const sectionGroup = nav?.trail[0] ?? NAV_TREE.find((g) => g.id === 'home')
  const title = pathname === '/' ? l({ en: 'Home', ar: 'الرئيسية' }) : nav ? l(nav.page.label) : decode(pathname.split('/').filter(Boolean).pop() ?? '/')
  return {
    title,
    hint: clip(hint),
    icon: (nav && pageIcon(nav.page.id, sectionGroup?.icon)) ?? sectionGroup?.icon ?? File,
    hue: hueOf(sectionGroup?.id ?? 'x'),
    section: sectionGroup ? l(sectionGroup.label) : '',
  }
}

export function useTabMeta(url: string): TabMeta {
  const { l, locale } = useT()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => tabMeta(url, l), [url, locale])
}

/**
 * Click handlers for any link that opens a page: a normal click opens (or focuses) its tab, Ctrl/⌘-click and
 * middle-click open it in the background. Shift/Alt-click keep the browser's own behaviour.
 */
export function useTabLink() {
  const open = useTabs((s) => s.open)
  return (url: string, after?: () => void) => ({
    onClick: (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.shiftKey || e.altKey) return
      e.preventDefault()
      const background = e.ctrlKey || e.metaKey
      open(url, { background })
      if (!background) after?.()
    },
    onAuxClick: (e: MouseEvent) => {
      if (e.button !== 1) return
      e.preventDefault()
      open(url, { background: true })
    },
    // no auto-scroll cursor on middle-press
    onMouseDown: (e: MouseEvent) => {
      if (e.button === 1) e.preventDefault()
    },
  })
}
